import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import PermissionDenied from "@/components/PermissionDenied";
import { SalaryAdvanceCreateDialog } from "@/components/hr/SalaryAdvanceCreateDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { useGridPagination } from "@/hooks/useGridPagination";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { formatAmountCommas } from "@/lib/formatPkrAmount";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import {
  deleteAdvanceSalaryBatch,
  groupAdvanceSalaryBatches,
  listAllSalaryAdvances,
  periodKey,
  periodLabel,
  salaryAdvancesApiErrorMessage,
  type AdvanceSalaryBatch,
} from "@/lib/hr/salaryAdvancesStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import {
  Banknote,
  CalendarRange,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  Search,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;
const ADVANCES_FETCH_CAP = 500;

const MONTHS = [
  { v: 1, label: "January" },
  { v: 2, label: "February" },
  { v: 3, label: "March" },
  { v: 4, label: "April" },
  { v: 5, label: "May" },
  { v: 6, label: "June" },
  { v: 7, label: "July" },
  { v: 8, label: "August" },
  { v: 9, label: "September" },
  { v: 10, label: "October" },
  { v: 11, label: "November" },
  { v: 12, label: "December" },
] as const;

type AdvanceSortBy = "updated_desc" | "updated_asc" | "period_desc" | "period_asc";

function employeeLabel(employees: EmployeeRecord[], employeeId: string): string {
  const e = employees.find((x) => x.id === employeeId);
  if (!e) return employeeId || "—";
  return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id;
}

function isActiveEmployee(e: EmployeeRecord): boolean {
  return (e.employeeStatus || "").toLowerCase() === "active";
}

function employeeBadge(employees: EmployeeRecord[], employeeId: string): { label: string; className: string } {
  const label = employeeLabel(employees, employeeId);
  const palettes = [
    "bg-sky-500/15 text-sky-700",
    "bg-emerald-500/15 text-emerald-700",
    "bg-violet-500/15 text-violet-700",
    "bg-amber-500/15 text-amber-700",
    "bg-rose-500/15 text-rose-700",
    "bg-teal-500/15 text-teal-700",
  ] as const;
  let h = 0;
  for (let i = 0; i < employeeId.length; i += 1) h = (h * 31 + employeeId.charCodeAt(i)) >>> 0;
  return { label, className: palettes[h % palettes.length] };
}

function batchPeriodSummary(batch: AdvanceSalaryBatch): string {
  return batch.lines
    .map((line) => periodLabel(line.periodYear, line.periodMonth, MONTHS))
    .join(" · ");
}

function batchStatusSummary(batch: AdvanceSalaryBatch): string {
  const deducted = batch.lines.filter((l) => l.status === "deducted").length;
  if (deducted === batch.lines.length) return "All deducted";
  if (deducted > 0) return `${deducted}/${batch.lines.length} deducted`;
  return "Pending";
}

export default function SalaryAdvances() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "salary_advance_can_create");
  const canDelete = can(user, "salary_advance_can_cancel");
  const [isForbidden, setIsForbidden] = useState(false);

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);
  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);

  const [batches, setBatches] = useState<AdvanceSalaryBatch[]>([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState<string>("all");
  const [sortBy, setSortBy] = useState<AdvanceSortBy>("updated_desc");
  const [quickDate, setQuickDate] = useState<"today" | "week" | "month" | "all">("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdvanceSalaryBatch | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(PAGE_SIZE_OPTIONS[0]);

  useEffect(() => {
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const res = await listAllSalaryAdvances({ page: 1, perPage: ADVANCES_FETCH_CAP });
      setBatches(groupAdvanceSalaryBatches(res.rows));
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setBatches([]);
        return;
      }
      toast.error(salaryAdvancesApiErrorMessage(e));
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return batches.filter((batch) => {
      if (filterEmployee !== "all" && batch.employeeId !== filterEmployee) return false;
      if (quickDate !== "all") {
        const updated = parseISO(batch.updatedAt);
        if (!isValid(updated)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (updated < start || updated > end) return false;
      }
      if (!q) return true;
      const empName = employeeLabel(employees, batch.employeeId).toLowerCase();
      const hay = `${empName} ${batch.totalAmount} ${batchPeriodSummary(batch)} ${batch.lines.map((l) => l.reason ?? "").join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [batches, query, filterEmployee, quickDate, employees]);

  const sortedFiltered = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      const aPeriod = a.lines[0] ? periodKey(a.lines[0].periodYear, a.lines[0].periodMonth) : "";
      const bPeriod = b.lines[0] ? periodKey(b.lines[0].periodYear, b.lines[0].periodMonth) : "";
      switch (sortBy) {
        case "updated_asc":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "period_desc":
          return bPeriod.localeCompare(aPeriod) || b.batchId.localeCompare(a.batchId);
        case "period_asc":
          return aPeriod.localeCompare(bPeriod) || a.batchId.localeCompare(b.batchId);
        case "updated_desc":
        default:
          return b.updatedAt.localeCompare(a.updatedAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount = filterEmployee !== "all" ? 1 : 0;
  const resetFilters = () => setFilterEmployee("all");

  const paginationKey = `${query}|${filterEmployee}|${quickDate}|${sortBy}|${pageSize}`;
  const { paginatedItems, totalCount, totalPages, rangeStart, rangeEnd } = useGridPagination(sortedFiltered, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => setModalOpen(true);

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const result = await deleteAdvanceSalaryBatch({
        employeeId: deleteTarget.employeeId,
        batchId: deleteTarget.batchId,
      });
      toast.success(result.message);
      setDeleteTarget(null);
      setPage(1);
      await load();
    } catch (e) {
      toast.error(salaryAdvancesApiErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Advance salary access required"
      message="You don’t have permission to view advance salary. Ask an administrator to grant salary_advance_can_view_any."
    />
  ) : (
    <Layout title="Advance salary">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Advance salary</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Record advance amounts by salary period — each line is deducted when that period&apos;s payroll is processed.
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" />
              Add advance salary
            </Button>
          ) : null}
        </div>

        <SalaryAdvanceCreateDialog
          open={modalOpen}
          onOpenChange={setModalOpen}
          onSaved={() => {
            setPage(1);
            void load();
          }}
        />

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete advance salary?"
          description={
            deleteTarget
              ? `This will remove the advance salary record for ${employeeLabel(employees, deleteTarget.employeeId)} (${formatAmountCommas(String(deleteTarget.totalAmount))} across ${deleteTarget.lines.length} period${deleteTarget.lines.length === 1 ? "" : "s"}).`
              : ""
          }
          confirmLabel={deleting ? "Deleting..." : "Delete"}
          onConfirm={() => void confirmDelete()}
        />

        <div className="glass-card p-6 space-y-4">
          <div className="space-y-1.5">
            <Label>Search</Label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="glass-input pl-9 h-10"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search employee, amount, period…"
                />
              </div>
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <Button type="button" variant="glass" className="gap-2 shrink-0">
                    <Filter className="w-4 h-4" />
                    Filters
                    {appliedFiltersCount > 0 ? (
                      <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        {appliedFiltersCount}
                      </span>
                    ) : null}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Filter advance salary</SheetTitle>
                    <SheetDescription>Refine by employee.</SheetDescription>
                  </SheetHeader>
                  <div className="mt-6 space-y-5">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Employee</Label>
                      <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All employees</SelectItem>
                          {activeEmployees.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                              {employeeLabel(employees, e.id)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <SheetFooter className="mt-6 gap-2">
                    <Button type="button" variant="glass" className="w-full" onClick={resetFilters}>
                      Reset
                    </Button>
                    <SheetClose asChild>
                      <Button type="button" variant="premium" className="w-full">
                        Apply filters
                      </Button>
                    </SheetClose>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div className="shrink-0">
              <GridPerPageSelect id="advance-page-size" pageSize={pageSize} onPageSizeChange={(n) => setPageSize(n as never)} options={PAGE_SIZE_OPTIONS} />
            </div>
            <div className="flex items-center justify-end gap-2 flex-wrap">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as AdvanceSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated_desc">Newest first</SelectItem>
                    <SelectItem value="updated_asc">Oldest first</SelectItem>
                    <SelectItem value="period_desc">Period: Newest first</SelectItem>
                    <SelectItem value="period_asc">Period: Oldest first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button type="button" onClick={() => setViewMode("grid")} className={cn("h-full px-3 text-sm transition-colors", viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50")} aria-label="Grid view">
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setViewMode("list")} className={cn("h-full px-3 text-sm transition-colors", viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50")} aria-label="List view">
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : null}

          {!loading && sortedFiltered.length === 0 ? (
            <div className="text-sm text-muted-foreground">No advance salary records found for these filters.</div>
          ) : null}

          {!loading && sortedFiltered.length > 0 && viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {paginatedItems.map((batch, i) => (
                <motion.div key={batch.batchId} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <CatalogEntityCard
                    title={`Advance · ${formatAmountCommas(String(batch.totalAmount))}`}
                    statusBadge={employeeBadge(employees, batch.employeeId)}
                    primaryMeta={{ label: "Periods", value: batchPeriodSummary(batch), icon: CalendarRange, iconClassName: "text-sky-600" }}
                    secondaryMeta={{ label: "Status", value: batchStatusSummary(batch), icon: Banknote, iconClassName: "text-emerald-600" }}
                    footer={`${batch.lines.length} line${batch.lines.length === 1 ? "" : "s"} · Updated ${formatCatalogAuditDate(batch.updatedAt)}`}
                    onDelete={canDelete && batch.canDelete ? () => setDeleteTarget(batch) : undefined}
                  />
                  <div className="mt-2 px-1 space-y-1">
                    {batch.lines.map((line) => (
                      <div key={line.id} className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {periodLabel(line.periodYear, line.periodMonth, MONTHS)} · {formatAmountCommas(line.advanceAmount)}
                        </span>
                        {line.status === "deducted" ? <Badge variant="secondary">Deducted</Badge> : <Badge variant="outline">Pending</Badge>}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : null}

          {!loading && sortedFiltered.length > 0 && viewMode === "list" ? (
            <div className="space-y-3">
              {paginatedItems.map((batch, i) => (
                <motion.div key={batch.batchId} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }} className="glass-card p-3">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="h-14 w-16 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                      <Banknote className="w-7 h-7 text-emerald-500/70" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-foreground truncate">
                        <span className={cn("mr-2 rounded-full px-2 py-0.5 text-[11px] font-semibold", employeeBadge(employees, batch.employeeId).className)}>
                          {employeeBadge(employees, batch.employeeId).label}
                        </span>
                        {formatAmountCommas(String(batch.totalAmount))}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{batchPeriodSummary(batch)}</p>
                      <div className="mt-2 space-y-1">
                        {batch.lines.map((line) => (
                          <p key={line.id} className="text-xs text-muted-foreground">
                            {periodLabel(line.periodYear, line.periodMonth, MONTHS)} · {formatAmountCommas(line.advanceAmount)}
                            {line.reason ? ` · ${line.reason}` : ""}
                            {line.status === "deducted" ? " · deducted" : " · pending"}
                          </p>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline">{batchStatusSummary(batch)}</Badge>
                      {canDelete && batch.canDelete ? (
                        <Button variant="ghost" size="sm" className="h-8 text-destructive" onClick={() => setDeleteTarget(batch)}>
                          Delete
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : null}

          {!loading && sortedFiltered.length > 0 ? (
            <GridPaginationFooter page={page} totalPages={totalPages} totalCount={totalCount} rangeStart={rangeStart} rangeEnd={rangeEnd} onPageChange={setPage} />
          ) : null}
        </div>
      </div>
    </Layout>
  );
}
